import { getUpdaterFunction, SetEditorStateFunction } from "@/contexts/editor-context-utils";
import { debugError } from "@/core/utils/print-utilts";
import { noop } from "lodash";
import { StripeSubscriptionFirestoreDoc, StripeSubscriptionItem } from "./stripe";
import {
  AppSubscriptionPlanType,
  StripeSubscriptionProductId,
  subscriptionPlanRank,
  subscriptionProductToPlan,
} from "./subscription";
import { StateUpdater } from "./utils";

export interface UserStripeSubscriptionsEditorState {
  userStripeSubscriptions: StripeSubscriptionFirestoreDoc[];
  setUserStripeSubscriptions: (value: StateUpdater<StripeSubscriptionFirestoreDoc[]>) => void;
}

export function getDummyUserStripeSubscriptionsEditorState(): UserStripeSubscriptionsEditorState {
  return {
    userStripeSubscriptions: [],
    setUserStripeSubscriptions: noop,
  };
}

export function getDefaultUserStripeSubscriptionsEditorState(
  set: SetEditorStateFunction,
): UserStripeSubscriptionsEditorState {
  return {
    userStripeSubscriptions: [],
    setUserStripeSubscriptions: getUpdaterFunction(set, "userStripeSubscriptions"),
  };
}

export function resetUserStripeSubscriptionsEditorState(state: UserStripeSubscriptionsEditorState) {
  state.setUserStripeSubscriptions([]);
}

const defaultExcludedPlans = [StripeSubscriptionProductId.ApiStandardPlan];

export function getHighestUserStripeSubscriptionItem({
  userStripeSubscriptions = [],
  excludePlans = defaultExcludedPlans,
}: {
  userStripeSubscriptions: StripeSubscriptionFirestoreDoc[];
  excludePlans?: StripeSubscriptionProductId[];
}): StripeSubscriptionItem | undefined {
  try {
    let subscriptionItems = userStripeSubscriptions.flatMap((subscription) => subscription.items);

    if (subscriptionItems.length === 0) {
      return undefined;
    }

    // Filter out items with excluded plans
    if (excludePlans && excludePlans.length > 0) {
      const excludePlansSet = new Set(excludePlans);
      subscriptionItems = subscriptionItems.filter(
        (item) => !excludePlansSet.has(item.plan.product as StripeSubscriptionProductId),
      );

      if (subscriptionItems.length === 0) {
        return undefined;
      }
    }

    // Sort the items based on subscriptionPlanRank
    const sortedItems = subscriptionItems.sort((itemA, itemB) => {
      // If the product isn't in our mapping, treat it as Enterprise
      const planA =
        itemA.plan.product in subscriptionProductToPlan
          ? subscriptionProductToPlan[itemA.plan.product as StripeSubscriptionProductId]
          : AppSubscriptionPlanType.Enterprise;

      const planB =
        itemB.plan.product in subscriptionProductToPlan
          ? subscriptionProductToPlan[itemB.plan.product as StripeSubscriptionProductId]
          : AppSubscriptionPlanType.Enterprise;

      return (
        subscriptionPlanRank[planB ?? AppSubscriptionPlanType.Enterprise] -
        subscriptionPlanRank[planA ?? AppSubscriptionPlanType.Enterprise]
      );
    });

    return sortedItems[0];
  } catch (error) {
    debugError("Error getting highest user stripe subscription item: ", error);
    return undefined;
  }
}

export function getHighestUserStripeSubscriptionProduct({
  userStripeSubscriptions = [],
  excludePlans,
}: {
  userStripeSubscriptions: StripeSubscriptionFirestoreDoc[];
  excludePlans?: StripeSubscriptionProductId[];
}): StripeSubscriptionProductId | undefined {
  try {
    const highestItem = getHighestUserStripeSubscriptionItem({
      userStripeSubscriptions,
      excludePlans,
    });
    return highestItem?.plan.product as StripeSubscriptionProductId;
  } catch (error) {
    debugError("Error getting highest user stripe subscription product: ", error);
    return undefined;
  }
}

export function getHighestUserStripeSubscriptionPlan({
  userStripeSubscriptions = [],
  excludePlans,
}: {
  userStripeSubscriptions: StripeSubscriptionFirestoreDoc[];
  excludePlans?: StripeSubscriptionProductId[];
}): AppSubscriptionPlanType {
  try {
    const highestItem = getHighestUserStripeSubscriptionItem({
      userStripeSubscriptions,
      excludePlans,
    });

    // If user has a subscription but it's not in our mapping, treat it as Enterprise
    // This handles the case for custom subscriptions
    if (
      highestItem &&
      !subscriptionProductToPlan[highestItem.plan.product as StripeSubscriptionProductId]
    ) {
      return AppSubscriptionPlanType.Enterprise;
    }
    const product = highestItem?.plan.product as StripeSubscriptionProductId;
    const plan = subscriptionProductToPlan[product] ?? AppSubscriptionPlanType.Free;

    return plan;
  } catch (error) {
    debugError("Error getting user stripe subscriptions: ", error);
    return AppSubscriptionPlanType.Free;
  }
}
